import type { LicenseTier } from '@/mocks/saLicensing';
import { licenseTypeLabels, formatStorage, formatBandwidth, formatUsers, formatRadios, formatBitrate } from '@/mocks/saLicensing';

interface LicenseComparisonProps {
  licenses: LicenseTier[];
  onClose: () => void;
}

function priceLabel(lic: LicenseTier): string {
  const parts: string[] = [];
  if (lic.priceMonthly > 0) parts.push(`$${lic.priceMonthly}/mes`);
  if (lic.priceAnnual > 0) parts.push(`$${lic.priceAnnual}/año`);
  if (lic.priceLifetime > 0) parts.push(`$${lic.priceLifetime} único`);
  return parts.length > 0 ? parts.join(' · ') : 'Gratis';
}

function checkIcon() {
  return <i className="ri-check-line text-emerald-500 text-sm"></i>;
}

function dashIcon() {
  return <i className="ri-close-line text-foreground-300 text-sm"></i>;
}

function boolCell(val: boolean) {
  return (
    <td className="text-center px-3 py-2.5 border-b border-background-200">
      {val ? checkIcon() : dashIcon()}
    </td>
  );
}

const featureRows = [
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'tv', label: 'TV Channel' },
  { key: 'apps', label: 'Apps Móviles' },
  { key: 'ai', label: 'Inteligencia Artificial' },
  { key: 'advertising', label: 'Publicidad' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'streaming', label: 'Streaming' },
  { key: 'api', label: 'API' },
  { key: 'customDomain', label: 'Dominio Propio' },
  { key: 'verification', label: 'Verificación' },
  { key: 'frequencyPreferred', label: 'Frec. Preferida' },
];

export default function LicenseComparison({ licenses, onClose }: LicenseComparisonProps) {
  const visibleLicenses = licenses.filter((l) => l.visible);

  if (visibleLicenses.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
        <div className="relative bg-background-50 rounded-2xl w-full max-w-md p-8 text-center shadow-xl">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-stack-line text-foreground-300 text-2xl"></i>
          </div>
          <p className="text-foreground-800 text-sm font-semibold mb-1">Sin licencias visibles</p>
          <p className="text-foreground-400 text-xs mb-4">Publicá al menos una licencia para ver la comparativa.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="shrink-0 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center shrink-0">
              <i className="ri-scales-3-line text-accent-600 text-lg"></i>
            </div>
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">Comparativa de Licencias</h3>
              <p className="text-foreground-400 text-[10px]">{visibleLicenses.length} licencias públicas comparadas</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Scrollable table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full min-w-[800px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-background-100">
                <th className="text-left px-4 py-3 text-foreground-500 text-[10px] font-bold uppercase tracking-wider border-b border-background-200 sticky left-0 bg-background-100">
                  Característica
                </th>
                {visibleLicenses.map((lic) => (
                  <th key={lic.id} className="text-center px-3 py-3 border-b border-background-200 min-w-[120px]" style={{ backgroundColor: `${lic.color}08` }}>
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-foreground-800 text-xs font-bold">{lic.name}</span>
                      <span className="text-foreground-400 text-[9px]">{licenseTypeLabels[lic.type]}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Pricing row */}
              <tr className="bg-amber-50/30">
                <td className="sticky left-0 bg-amber-50/30 px-4 py-2.5 text-foreground-600 text-[10px] font-bold border-b border-background-200 whitespace-nowrap">
                  <i className="ri-money-dollar-circle-line mr-1.5 text-amber-600"></i>
                  Precio
                </td>
                {visibleLicenses.map((lic) => (
                  <td key={lic.id} className="text-center px-3 py-2.5 border-b border-background-200">
                    <span className="text-foreground-800 text-[10px] font-bold">{priceLabel(lic)}</span>
                  </td>
                ))}
              </tr>

              {/* Limits rows */}
              <tr>
                <td className="sticky left-0 bg-background-50 px-4 py-2.5 text-foreground-500 text-[10px] font-medium border-b border-background-200 whitespace-nowrap">
                  <i className="ri-radio-line mr-1.5 text-foreground-400"></i>
                  Radios
                </td>
                {visibleLicenses.map((lic) => (
                  <td key={lic.id} className="text-center px-3 py-2.5 border-b border-background-200">
                    <span className="text-foreground-700 text-[10px] font-mono">{formatRadios(lic.maxRadios)}</span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="sticky left-0 bg-background-50 px-4 py-2.5 text-foreground-500 text-[10px] font-medium border-b border-background-200 whitespace-nowrap">
                  <i className="ri-user-line mr-1.5 text-foreground-400"></i>
                  Usuarios
                </td>
                {visibleLicenses.map((lic) => (
                  <td key={lic.id} className="text-center px-3 py-2.5 border-b border-background-200">
                    <span className="text-foreground-700 text-[10px] font-mono">{formatUsers(lic.maxUsers)}</span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="sticky left-0 bg-background-50 px-4 py-2.5 text-foreground-500 text-[10px] font-medium border-b border-background-200 whitespace-nowrap">
                  <i className="ri-hard-drive-2-line mr-1.5 text-foreground-400"></i>
                  Storage
                </td>
                {visibleLicenses.map((lic) => (
                  <td key={lic.id} className="text-center px-3 py-2.5 border-b border-background-200">
                    <span className="text-foreground-700 text-[10px] font-mono">{formatStorage(lic.storageGB)}</span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="sticky left-0 bg-background-50 px-4 py-2.5 text-foreground-500 text-[10px] font-medium border-b border-background-200 whitespace-nowrap">
                  <i className="ri-speed-up-line mr-1.5 text-foreground-400"></i>
                  Bandwidth
                </td>
                {visibleLicenses.map((lic) => (
                  <td key={lic.id} className="text-center px-3 py-2.5 border-b border-background-200">
                    <span className="text-foreground-700 text-[10px] font-mono">{formatBandwidth(lic.bandwidthGB)}</span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="sticky left-0 bg-background-50 px-4 py-2.5 text-foreground-500 text-[10px] font-medium border-b border-background-200 whitespace-nowrap">
                  <i className="ri-sound-module-line mr-1.5 text-foreground-400"></i>
                  Bitrate
                </td>
                {visibleLicenses.map((lic) => (
                  <td key={lic.id} className="text-center px-3 py-2.5 border-b border-background-200">
                    <span className="text-foreground-700 text-[10px] font-mono">{formatBitrate(lic.streamingBitrateKbps)}</span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="sticky left-0 bg-background-50 px-4 py-2.5 text-foreground-500 text-[10px] font-medium border-b border-background-200 whitespace-nowrap">
                  <i className="ri-timer-line mr-1.5 text-foreground-400"></i>
                  Trial
                </td>
                {visibleLicenses.map((lic) => (
                  <td key={lic.id} className="text-center px-3 py-2.5 border-b border-background-200">
                    <span className="text-foreground-700 text-[10px] font-mono">
                      {lic.trialDays > 0 ? `${lic.trialDays} días` : 'N/A'}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Divider */}
              <tr>
                <td colSpan={visibleLicenses.length + 1} className="px-4 py-2 border-b border-background-200">
                  <span className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider">Features</span>
                </td>
              </tr>

              {/* Feature rows */}
              {featureRows.map((row) => (
                <tr key={row.key}>
                  <td className="sticky left-0 bg-background-50 px-4 py-2.5 text-foreground-500 text-[10px] font-medium border-b border-background-200 whitespace-nowrap">
                    {row.label}
                  </td>
                  {visibleLicenses.map((lic) =>
                    boolCell((lic as Record<string, unknown>)[row.key] as boolean),
                  )}
                </tr>
              ))}

              {/* Feature Flags count */}
              <tr>
                <td className="sticky left-0 bg-background-50 px-4 py-2.5 text-foreground-500 text-[10px] font-medium border-b border-background-200 whitespace-nowrap">
                  <i className="ri-toggle-line mr-1.5 text-foreground-400"></i>
                  Feature Flags
                </td>
                {visibleLicenses.map((lic) => (
                  <td key={lic.id} className="text-center px-3 py-2.5 border-b border-background-200">
                    <span className="text-foreground-700 text-[10px] font-mono">{lic.featureFlags.length}</span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="shrink-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-between rounded-b-2xl">
          <p className="text-foreground-400 text-[10px]">
            Vista previa pública — {visibleLicenses.length} de {licenses.length} licencias
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar comparativa
          </button>
        </div>
      </div>
    </div>
  );
}